/**
 * Configuración de arranque (RECI-F00).
 *
 * El contenedor de release genera `assets/runtime-config.json` a partir de variables de
 * entorno, para que un mismo bundle sirva a cualquier destino. Lo que faltaba era esto: el
 * lado que lo LEE. Sin ello el fichero se generaba y no lo miraba nadie, así que la
 * aplicación seguía hablando con lo que se compiló en `environment.prod.ts` — el CMS del
 * tronco, no el del destino.
 *
 * Las reglas del volcado están pensadas para que un destino SIN fichero se comporte
 * exactamente como antes:
 *
 *   - sin fichero, con error de red o con un contenido que no es JSON válido, se arranca con
 *     lo compilado;
 *   - solo se pisan claves que YA existen en el destino y que allí son texto, así que una
 *     clave de más en la plantilla no inventa configuración ni cambia un interruptor;
 *   - un valor vacío no pisa: una variable sin definir en el despliegue deja el valor
 *     compilado en vez de dejar la URL en blanco, que es el fallo que no daría ningún error
 *     y rompería todas las llamadas.
 */
export const RUNTIME_CONFIG_URL = 'assets/runtime-config.json';

export async function applyRuntimeConfig(
  target: Record<string, unknown>,
  url: string = RUNTIME_CONFIG_URL,
): Promise<void> {
  try {
    // `no-store`: el fichero se genera al arrancar el contenedor, así que una copia de la
    // caché del navegador es justo la configuración del despliegue anterior.
    const response = await fetch(url, { cache: 'no-store' });

    if (!response.ok) {
      return;
    }

    const config: unknown = await response.json();

    if (!config || typeof config !== 'object' || Array.isArray(config)) {
      return;
    }

    for (const [key, value] of Object.entries(config as Record<string, unknown>)) {
      if (typeof value !== 'string' || value === '') {
        continue;
      }

      if (typeof target[key] !== 'string') {
        continue;
      }

      target[key] = value;
    }
  } catch {
    /* sin configuración de arranque: se sigue con lo compilado */
  }
}
